import { Injectable } from '@angular/core';

import {
  SyncExporter,
  ExporterDescriptor,
  ExportArtifact
} from '../models/exporter';
import {
  ExportPayload,
  ExportTaskRecord
} from '../models/export-payload';
import { EXTRA_COLUMNS_CSV_XLSX } from './extras-utils';

/**
 * Exporter CSV (síncrono, 100% en cliente).
 *
 * Estándar 2026:
 *   - RFC 4180 estricto: separador coma, line endings CRLF, quoting bajo demanda.
 *   - UTF-8 con BOM (3 bytes EF BB BF). Excel 365+, LibreOffice, Numbers, pandas
 *     y polars lo aceptan sin problema; el BOM resuelve el viejo bug de
 *     acentos rotos en versiones antiguas de Excel.
 *   - Sin opciones de delimitador en el panel: la UX limpia manda.
 *
 * Provenance: la columna `origen` lleva la etiqueta humana ("IA",
 * "IA (Modificada)", "Histórica", …) — trazabilidad legible en cualquier
 * herramienta que abra el CSV, sin necesidad de un diccionario al lado.
 */
@Injectable({ providedIn: 'root' })
export class CsvExporter implements SyncExporter {

  readonly descriptor: ExporterDescriptor & { mode: 'sync' } = {
    id: 'csv',
    label: 'CSV',
    shortHint: 'Archivo · RFC 4180',
    iconSvgPath:
      'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z',
    mode: 'sync',
    requiredPermission: 'estimation:export',
    supportsExtras: true,
    order: 10
  };

  /** Columnas del CSV en orden, con su mapeo desde ExportTaskRecord. */
  private readonly columns: Array<{
    header: string;
    pick: (t: ExportTaskRecord) => unknown;
  }> = [
    { header: 'id',         pick: t => t.id },
    { header: 'bloque',     pick: t => t.requirementDescription },
    { header: 'tarea',      pick: t => t.description },
    { header: 'tipo',       pick: t => t.type },
    { header: 'subsistema', pick: t => t.subsystem },
    { header: 'stack',      pick: t => t.technology },
    { header: 'perfil',     pick: t => t.profile },
    { header: 'horas',      pick: t => t.hours },
    { header: 'p10',        pick: t => t.p10Hours ?? '' },
    { header: 'p90',        pick: t => t.p90Hours ?? '' },
    { header: 'confianza',  pick: t => t.confidence ?? '' },
    { header: 'origen',     pick: t => t.provenanceLabel }
  ];

  async generate(payload: ExportPayload, _options: unknown): Promise<ExportArtifact> {
    const csv = this.serialize(payload);

    // BOM (EF BB BF) prepended a UTF-8 — máxima compatibilidad lectores.
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });

    return {
      filename: this.buildFilename(payload),
      mimeType: 'text/csv',
      blob
    };
  }

  // ==========================================================================
  // INTERNO
  // ==========================================================================

  private serialize(payload: ExportPayload): string {
    // Si el payload viene enriquecido (toggle "completo" activo), concatenamos
    // las columnas extras del helper centralizado. DRY total — el mismo
    // EXTRA_COLUMNS_CSV_XLSX se usa también en xlsx.exporter.ts.
    const effectiveColumns = payload.extrasIncluded
      ? [...this.columns, ...EXTRA_COLUMNS_CSV_XLSX]
      : this.columns;

    const headerRow = effectiveColumns
      .map(c => this.escapeCell(c.header))
      .join(',');

    const dataRows = payload.tasks.map(task =>
      effectiveColumns.map(c => this.escapeCell(c.pick(task))).join(',')
    );

    // CRLF entre filas (RFC 4180). Sin newline final — pandas y Excel
    // son tolerantes; añadirlo introduce una fila vacía en algunos parsers.
    return [headerRow, ...dataRows].join('\r\n');
  }

  /**
   * Escapado RFC 4180:
   *   - Si el valor contiene coma, comilla doble, CR o LF → se rodea con comillas.
   *   - Las comillas dobles internas se duplican.
   *   - Resto de valores se serializan tal cual (no rodeamos números).
   */
  private escapeCell(value: unknown): string {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (!/[",\r\n]/.test(s)) return s;
    return '"' + s.replace(/"/g, '""') + '"';
  }

  private buildFilename(payload: ExportPayload): string {
    const ts = this.timestampSlug(payload.estimation.exportedAt);
    const id = payload.estimation.id;
    return `estimacion-${id}-${ts}.csv`;
  }

  /** "2026-06-06T12:34:56.789Z" → "20260606-1234" (hora local). */
  private timestampSlug(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      '-' +
      pad(d.getHours()) +
      pad(d.getMinutes())
    );
  }
}